import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export const formatBalanceCents = (amountCents: number): string =>
    new Intl.NumberFormat('uk-UA', {
        style: 'currency',
        currency: 'UAH',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(Math.floor(amountCents / 100));

const formatMoneyCents = (amountCents: number): string =>
    new Intl.NumberFormat('uk-UA', {
        style: 'currency',
        currency: 'UAH',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(amountCents / 100);

export const formatBalanceWithPendingFees = (
    balanceCents: number,
    pendingServiceFeesCents: number,
): string => {
    const balance = formatBalanceCents(balanceCents);
    if (balanceCents !== 0 || pendingServiceFeesCents <= 0) return balance;

    return `${balance} (до сплати: ${formatMoneyCents(pendingServiceFeesCents)})`;
};

// Функція для форматування дати в локальний час
export const formatLocalDateTime = (dateString: string | undefined): string => {
    if (!dateString) return '';

    try {
        const date = new Date(dateString);
        return date.toLocaleString('uk-UA', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        });
    } catch {
        return dateString; // Повертаємо оригінальний рядок, якщо не вдалося розпарсити
    }
};

export const formatLocalDate = (dateString: string | undefined): string => {
    if (!dateString) return '';

    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('uk-UA', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
    } catch {
        return dateString;
    }
};

// Get sales count text with proper declension
export const getSalesCountText = (count: number): string => {
    const lastDigit = count % 10;
    const lastTwoDigits = count % 100;

    if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
        return `${count} продажів`;
    }

    if (lastDigit === 1) {
        return `${count} продаж`;
    }

    if (lastDigit >= 2 && lastDigit <= 4) {
        return `${count} продажі`;
    }

    return `${count} продажів`;
};

export const getReviewsCountText = (count: number): string => {
    const lastDigit = count % 10;
    const lastTwoDigits = count % 100;

    if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
        return `${count} відгуків`;
    }

    if (lastDigit === 1) {
        return `${count} відгук`;
    }

    if (lastDigit >= 2 && lastDigit <= 4) {
        return `${count} відгуки`;
    }

    return `${count} відгуків`;
};

export const getProductsCountText = (count: number): string => {
    const lastDigit = count % 10;
    const lastTwoDigits = count % 100;

    if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
        return `${count} товарів`;
    }

    if (lastDigit === 1) {
        return `${count} товар`;
    }

    if (lastDigit >= 2 && lastDigit <= 4) {
        return `${count} товари`;
    }

    return `${count} товарів`;
};

export const formatReviewScore = (score?: number | null): string => {
    if (!score || score <= 0) return '0';

    const formattedScore = score.toFixed(1);

    return formattedScore.endsWith('.0')
        ? formattedScore.slice(0, -2)
        : formattedScore;
};

export const formatDate = (date: string): string =>
    new Intl.DateTimeFormat('uk-UA', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    }).format(new Date(date));
